import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import { Icon } from '../icon'
import ReleaseNoteIllustration from './ReleaseNoteIllustration'
import StatusTag from '../tag/StatusTag'

// Utils
import useReleaseNotes from '../../hooks/useReleaseNotes'

// Styles
import { hsla } from '../../styles/colors'
import { mqFrom } from '../../styles/mediaQueries'

// Types
import {
  IReleaseNote,
  IReleaseNoteItem,
  ReleaseNoteAudience,
} from '../../common/models/ReleaseNote'
import { AnyColor } from '../../common/models/colors'
import { IconType } from '../icon/IconType'

interface IProps {
  audience?: ReleaseNoteAudience
  note: IReleaseNote
  // Below L the open list replaces the note
  isHidden: boolean
}

interface ISection {
  label: string
  fill: AnyColor
  textColor: AnyColor
  items?: IReleaseNoteItem[]
}

// One release note for the viewed role: date, title, intro, illustration and
// the New / Improved / Fixed sections, with company terminology resolved.
// Scrolls on its own and starts at the top when another note is selected.
const ReleaseNoteDetail: React.FC<IProps> = ({ audience, note, isHidden }) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const { language, contentOf } = useReleaseNotes(false, audience)
  const content = contentOf(note)

  const sections: ISection[] = [
    {
      label: t('general.states.new'),
      fill: 'primary',
      textColor: 'lightest',
      items: content?.sections?.new,
    },
    {
      label: t('releaseNotes.improved'),
      fill: 'primaryFadeLight',
      textColor: 'primary',
      items: content?.sections?.improved,
    },
    {
      label: t('releaseNotes.fixed'),
      fill: 'textLightest',
      textColor: 'textDark',
      items: content?.sections?.fixed,
    },
  ]

  return (
    <Column $isHidden={isHidden}>
      <ScrollWrapper>
        {/* Keyed on the note so switching notes starts at the top */}
        <ScrollArea key={note.id}>
          <Content>
            {note.draft ? (
              <StatusTag caption={t('releaseNotes.draft')} />
            ) : (
              <Text textStyle="label" color="textLight">
                {new Date(note.publishedAt).toLocaleDateString(language, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Text>
            )}
            <Text textStyle="displayM" tag="h2" marginTop={8}>
              {content?.title}
            </Text>
            {content?.intro && (
              <Intro textStyle="body" color="textDark" marginTop={12}>
                {content.intro}
              </Intro>
            )}

            {content?.illustration && (
              <Illustration name={content.illustration} />
            )}

            {sections
              .filter((section) => section.items?.length)
              .map((section) => (
                <Section key={section.label}>
                  <SectionHeader>
                    <Pill $fill={section.fill}>
                      <Text
                        textStyle="labelSmallCaps"
                        color={section.textColor}
                      >
                        {section.label}
                      </Text>
                    </Pill>
                    <Rule />
                  </SectionHeader>
                  <Items>
                    {section.items!.map((item) => (
                      <Item key={item.title}>
                        <IconTile>
                          {item.icon && (
                            <Icon
                              type={item.icon as IconType}
                              size={20}
                              color={theme.colors.primary}
                            />
                          )}
                        </IconTile>
                        <div>
                          <Text textStyle="title" tag="h3">
                            {item.title}
                          </Text>
                          <Text
                            textStyle="bodySmall"
                            color="textDark"
                            marginTop={4}
                          >
                            {item.body}
                          </Text>
                          {!!item.points?.length && (
                            <Points>
                              {item.points.map((point) => (
                                <Point key={point}>
                                  <PointIcon>
                                    <Icon
                                      type="Check"
                                      size={12}
                                      color={theme.colors.primary}
                                    />
                                  </PointIcon>
                                  <Text textStyle="bodySmall" color="textDark">
                                    {point}
                                  </Text>
                                </Point>
                              ))}
                            </Points>
                          )}
                        </div>
                      </Item>
                    ))}
                  </Items>
                </Section>
              ))}
          </Content>
        </ScrollArea>
        <Fade />
      </ScrollWrapper>
    </Column>
  )
}

export default ReleaseNoteDetail

const Column = styled.div<{ $isHidden: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
  min-height: 0;

  ${mqFrom.L`
    display: flex;
  `}
`

const ScrollWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  min-height: 0;
`

const ScrollArea = styled.div`
  position: absolute;
  inset: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.textLighter} transparent;
`

const Content = styled.div`
  padding: 32px 16px 48px;

  ${mqFrom.M`
    padding: 40px 32px 56px;
  `}

  ${mqFrom.L`
    padding: 40px 48px 56px;
  `}
`

const Intro = styled(Text)`
  max-width: 560px;
`

const Illustration = styled(ReleaseNoteIllustration)`
  margin-top: 32px;
`

const Section = styled.section`
  margin-top: 32px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Pill = styled.span<{ $fill: AnyColor }>`
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: ${({ theme, $fill }) => theme.colors[$fill]};
`

const Rule = styled.span`
  flex-grow: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.textLightest};
`

const Items = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
`

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`

const Points = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
`

const Point = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

// Centers the check on the first 20px line of bodySmall text
const PointIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 20px;
`

const IconTile = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primaryFadeLight};
`

// Hints that the note continues below the fold
const Fade = styled.div`
  position: absolute;
  left: 0;
  right: 12px;
  bottom: 0;
  height: 40px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => hsla(theme.colors.lightest, 0)},
    ${({ theme }) => theme.colors.lightest}
  );
`
